import { competitionRanks } from '@topmeeps/shared'
import { del as idbDel, get as idbGet, set as idbSet } from 'idb-keyval'
import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export type DraftParticipant = {
  userId: string | null
  guestId: string | null
  displayName: string
  avatarHue: number
  // Array order is the canonical finishing order (best first). `rank` is a
  // derived standard-competition rank (1,1,3 …) kept in sync with the order
  // and `tiedWithPrevious` flags, so downstream submit/display can read it
  // directly. `tiedWithPrevious` means "finished level with the player above".
  rank: number
  tiedWithPrevious: boolean
  score: number | null
  subscores: Record<string, number>
  outcomeTags: string[]
}

export type RecordPlayDraft = {
  groupId: string | null
  gameId: string | null
  participants: DraftParticipant[]
  playedAt: string
  durationMinutes: number | null
  location: string
  notes: string

  setGame: (gameId: string, defaultDurationMinutes?: number | null) => void
  setParticipants: (p: DraftParticipant[]) => void
  // Move a participant one slot up (-1) or down (1) in the finishing order.
  reorderParticipant: (key: string, dir: -1 | 1) => void
  // Toggle whether a participant finished tied with the player directly above.
  toggleTie: (key: string) => void
  setScore: (key: string, score: number | null) => void
  setSubscore: (key: string, label: string, value: number) => void
  setDetails: (
    d: Partial<Pick<RecordPlayDraft, 'playedAt' | 'durationMinutes' | 'location' | 'notes'>>,
  ) => void
  reset: (groupId: string | null) => void
}

const keyOf = (p: DraftParticipant): string => p.userId ?? p.guestId ?? p.displayName

// Re-derive each participant's `rank` from the array order + tie flags so the
// stored rank is always a valid standard-competition ranking. The leader can
// never be "tied with previous", so force that flag false at index 0.
const withSyncedRanks = (participants: DraftParticipant[]): DraftParticipant[] => {
  const ranks = competitionRanks(participants.map((p, i) => i > 0 && p.tiedWithPrevious === true))
  return participants.map((p, i) => ({
    ...p,
    tiedWithPrevious: i > 0 && p.tiedWithPrevious === true,
    rank: ranks[i] ?? i + 1,
  }))
}

const blankDraft = (groupId: string | null) => ({
  groupId,
  gameId: null,
  participants: [],
  playedAt: new Date().toISOString(),
  durationMinutes: null,
  location: '',
  notes: '',
})

export const useRecordPlayDraft = create<RecordPlayDraft>()(
  persist(
    (set) => ({
      ...blankDraft(null),

      setGame: (gameId, defaultDurationMinutes) =>
        set((state) => ({
          gameId,
          // Pre-fill duration with the BGG-suggested time only if the
          // user hasn't typed one yet — don't clobber explicit edits when
          // they navigate back to swap games.
          durationMinutes:
            state.durationMinutes ??
            (defaultDurationMinutes != null ? defaultDurationMinutes : null),
        })),

      setParticipants: (participants) => set({ participants: withSyncedRanks(participants) }),

      reorderParticipant: (key, dir) =>
        set((state) => {
          const idx = state.participants.findIndex((p) => keyOf(p) === key)
          const swapWith = idx + dir
          if (idx < 0 || swapWith < 0 || swapWith >= state.participants.length) return state
          const next = [...state.participants]
          const a = next[idx]!
          const b = next[swapWith]!
          // Reordering separates these two players, so any tie spanning the
          // boundary they crossed no longer makes sense — clear both flags and
          // let the rest re-derive.
          next[idx] = { ...b, tiedWithPrevious: false }
          next[swapWith] = { ...a, tiedWithPrevious: false }
          return { participants: withSyncedRanks(next) }
        }),

      toggleTie: (key) =>
        set((state) => ({
          participants: withSyncedRanks(
            state.participants.map((p) =>
              keyOf(p) === key ? { ...p, tiedWithPrevious: !p.tiedWithPrevious } : p,
            ),
          ),
        })),

      setScore: (key, score) =>
        set((state) => ({
          participants: state.participants.map((p) => (keyOf(p) === key ? { ...p, score } : p)),
        })),

      setSubscore: (key, label, value) =>
        set((state) => ({
          participants: state.participants.map((p) =>
            keyOf(p) === key ? { ...p, subscores: { ...p.subscores, [label]: value } } : p,
          ),
        })),

      setDetails: (d) => set(d),

      reset: (groupId) => set(blankDraft(groupId)),
    }),
    {
      name: 'topmeeps:record-play-draft',
      storage: {
        getItem: async (name) => {
          const raw = await idbGet<string>(name)
          return raw ? JSON.parse(raw) : null
        },
        setItem: async (name, value) => {
          await idbSet(name, JSON.stringify(value))
        },
        removeItem: async (name) => {
          await idbDel(name)
        },
      },
    },
  ),
)

export const draftKeyOf = keyOf
